import { CampaignTile } from "@/types/checkout";
import { Campaign } from "@/types/api";
import { MONTH_NAMES } from "@/shared/constants/date.const";

export const calculateAvailableSelections = (
  tiles: CampaignTile[],
  maxLimit: number,
): number => {
  const alreadyBookedByUser = tiles.filter((tile) => tile.alreadyBooked).length;
  return Math.max(0, maxLimit - alreadyBookedByUser);
};

export const formatPrice = (price: number): string => {
  return `$${price.toLocaleString("en-US", { minimumFractionDigits: 2 })}`;
};

export const isFutureMonth = (monthStr: string): boolean => {
  const [year, month] = monthStr.split("-").map(Number);
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1; // 1-indexed
  return year > currentYear || (year === currentYear && month > currentMonth);
};

const isWithinDays = (monthStr: string, days: number): boolean => {
  const [year, month] = monthStr.split("-").map(Number);
  const targetDate = new Date(year, month - 1, 1);
  const now = new Date();
  const diffMs = targetDate.getTime() - now.getTime();
  const diffDays = diffMs / (1000 * 60 * 60 * 24);
  return diffDays <= days;
};

export const isAlreadyBookedByUser = (campaign: Campaign): boolean => {
  if (campaign.campaignStatus === "SCHEDULED") return true;
  if (
    campaign.campaignStatus === "ACTIVE" &&
    isWithinDays(campaign.campaignMonth, 14)
  ) {
    return true;
  }
  return false;
};

export const formatMonthYear = (
  monthStr: string,
): { month: string; year: string } => {
  const [year, monthNum] = monthStr.split("-");
  const date = new Date(parseInt(year), parseInt(monthNum) - 1, 1);
  const monthName = date.toLocaleString("en-US", { month: "long" });
  return { month: monthName, year };
};

export const maskEmail = (emailAddress: string) => {
  const [localPart, domain] = emailAddress.split("@");
  if (!domain) return emailAddress;
  const maskedLocal =
    localPart.length > 2
      ? `${localPart[0]}${"*".repeat(localPart.length - 2)}${localPart[localPart.length - 1]}`
      : localPart;
  return `${maskedLocal}@${domain}`;
};

export const sanitizeName = (value: string): string =>
  value.replace(/[^a-zA-Z\s\-']/g, "");

export const sanitizeEmail = (value: string): string =>
  value.replace(/[^a-zA-Z0-9._%+\-@]/g, "");

export const formatCampaignMonth = (month: string, year: string): string => {
  const monthIndex = Object.entries(MONTH_NAMES).find(
    ([, name]) => name === month,
  )?.[0];

  if (!monthIndex) {
    throw new Error(`Invalid month name: ${month}`);
  }

  const monthStr = String(monthIndex).padStart(2, "0");
  return `${year}-${monthStr}`;
};
